import React, {useEffect, useState, useMemo} from 'react';
import './index.scss';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import MetricsTable from '../../components/Dashboard/MetricsTable';
import StatisticBlock from '../../components/Dashboard/StatisticBlock';
import moment from 'moment/moment';
import TinyBarChart from '../../components/Charts/TinyBarChart';
import {useTranslation} from 'react-i18next';
import AverageTimeBar from '../../components/Charts/AverageTimeBar';
import i18n from '../../utils/i18n';
import {
  tableHead3MockData,
  tableHead4MockData,
  tableHead5MockData,
  tableHead6MockData,
  tableHeadMockData,
} from './mockData';
import {
  useGetEquipmentTypeQuery,
  useGetUsersRolesQuery,
  useLazyDashboardPersonalInfoQuery,
  useLazyDashboardPersonalStatsQuery,
  useLazyGetAgencyByIdQuery,
  useLazyMetricsAverageInspectionDurationQuery,
  useLazyMetricsCorrectiveActionsStatsQuery,
  useLazyMetricsEquipmentCountTypeQuery,
  useLazyMetricsEquipmentDecommissionsQuery,
  useLazyMetricsEquipmentRemovedQuery,
  useLazyMetricsEquipmentRemovedYesterdayQuery,
  useLazyMetricsInspectionByEquipmentTypeQuery,
  useLazyMetricsInspectionsMissedQuery,
  useLazyMetricsInspectionsPlannedQuery,
  useLazyMetricsInspectionsYesterdayQuery,
  useLazyDashboardAveragePerInspectionQuery,
} from '../../store/builder/inspections.api';
import {setLoader} from '../../store/builder/reducer';
import {useDispatch} from 'react-redux';
import log from '../../SVG/Log';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {Helmet} from 'react-helmet';

const colors = ['#548CC5', '#2AA58D', '#DF9800', '#E9EAEB'];

const AdvancedMetrics = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const corporateAgency = searchParams.get('agency');

  const currentFbo = +corporateAgency || localStorage.getItem('current-fbo');

  const filterPeriodOptionsInitial = useMemo(
    () => [
      {value: '', label: t('advancedMetrics.all')},
      {value: 'daily', label: t('advancedMetrics.daily')},
      {value: 'weekly', label: t('advancedMetrics.weekly')},
      {value: 'monthly', label: t('advancedMetrics.monthly')},
      {value: 'quarterly', label: t('advancedMetrics.quarterly')},
      {value: 'semiannual', label: t('advancedMetrics.semiannual')},
      {value: 'annual', label: t('advancedMetrics.annual')},
    ],
    [t],
  );
  const filterPeriodShortOptionsInitial = useMemo(
    () => [
      {value: 'weekly', label: t('advancedMetrics.weekly')},
      {value: 'monthly', label: t('advancedMetrics.monthly')},
      {value: 'quarterly', label: t('advancedMetrics.quarterly')},
      {value: 'semiannual', label: t('advancedMetrics.semiannual')},
    ],
    [t],
  );

  const [filterPeriodOptions, setFilterPeriodOptions] = useState(
    filterPeriodOptionsInitial,
  );
  const [filterPeriodShortOptions, setFilterPeriodShortOptions] = useState(
    filterPeriodShortOptionsInitial,
  );

  const [chosenMissedFilters, setChosenMissedFilters] = useState({
    period: null,
    filter_for_period: 'weekly',
  });
  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([]);

  const [averageFilters, setAverageFilters] = useState({
    filter_period: filterPeriodOptions[0].value,
    facility_id: null,
    equipment_type_id: null,
    employee_id: null,
  });

  // Update filter options when translations are loaded
  // This ensures options are updated when translations become available after initial render
  useEffect(() => {
    const updateOptions = () => {
      if (i18n.isInitialized) {
        // Check if translations are actually working (not just returning keys)
        const testTranslation = t('advancedMetrics.lastWeek');
        if (testTranslation && testTranslation !== 'advancedMetrics.lastWeek') {
          // Update filterPeriodOptions
          setFilterPeriodOptions([
            {value: '', label: t('advancedMetrics.all')},
            {value: 'daily', label: t('advancedMetrics.daily')},
            {value: 'weekly', label: t('advancedMetrics.weekly')},
            {value: 'monthly', label: t('advancedMetrics.monthly')},
            {value: 'quarterly', label: t('advancedMetrics.quarterly')},
            {value: 'semiannual', label: t('advancedMetrics.semiannual')},
            {value: 'annual', label: t('advancedMetrics.annual')},
          ]);

          // Update filterPeriodShortOptions
          setFilterPeriodShortOptions([
            {value: 'weekly', label: t('advancedMetrics.weekly')},
            {value: 'monthly', label: t('advancedMetrics.monthly')},
            {value: 'quarterly', label: t('advancedMetrics.quarterly')},
            {value: 'semiannual', label: t('advancedMetrics.semiannual')},
          ]);
        }
      }
    };

    // Update immediately if i18n is already initialized
    updateOptions();

    // Listen for translation loading events
    const handleTranslationsLoaded = () => {
      updateOptions();
    };

    if (typeof i18n.on === 'function') {
      i18n.on('loaded', handleTranslationsLoaded);
      i18n.on('initialized', handleTranslationsLoaded);
      i18n.on('languageChanged', handleTranslationsLoaded);
    }

    return () => {
      if (typeof i18n.off === 'function') {
        i18n.off('loaded', handleTranslationsLoaded);
        i18n.off('initialized', handleTranslationsLoaded);
        i18n.off('languageChanged', handleTranslationsLoaded);
      }
    };
  }, [t]);

  function filterNullValues(obj) {
    const newObj = {};
    for (const key in obj) {
      if (obj[key]) {
        newObj[key] = obj[key];
      }
    }
    return newObj;
  }

  // Map AdvancedDashboard filter period values to API-expected values (dashboard/average-time)
  const mapFilterPeriodForAverageTime = value => {
    const map = {
      '': 'week',
      daily: 'daily',
      weekly: 'week',
      monthly: 'month',
      quarterly: 'quarter',
      semiannual: 'semiannual',
      annual: 'annual',
    };
    return map[value] ?? 'week';
  };

  useEffect(() => {
    const rawPeriod =
      averageFilters?.filter_period ||
      filterPeriodOptions[0]?.value ||
      'weekly';
    const filterPeriod = mapFilterPeriodForAverageTime(rawPeriod);
    let obj = {
      filter_period: filterPeriod,
      facility_id: averageFilters?.facility_id,
      equipment_type_id: averageFilters?.equipment_type_id,
      employee_id: averageFilters?.employee_id,
      ...(currentFbo && {agency_id: currentFbo}),
    };

    const newObj = filterNullValues(obj);

    let params = new URLSearchParams(newObj).toString();
    getDashboardAveragePerInspection(params);
  }, [averageFilters, currentFbo]);

  const {data: equipmentTypesData, isSuccess: equipmentTypesIsSuccess} =
    useGetEquipmentTypeQuery();

  const {data: userRolesData, isSuccess: userRolesIsSuccess} =
    useGetUsersRolesQuery();

  const [getMetricsPlanned, {isFetching: metricsPlannedIsLoading}] =
    useLazyMetricsInspectionsPlannedQuery();

  const [
    getDashboardAveragePerInspection,
    {data: dashboardAveragePerInspectionData},
  ] = useLazyDashboardAveragePerInspectionQuery();

  const [
    getMetricsYesterday,
    {data: metricsYesterdayData, isFetching: metricsYesterdayIsLoading},
  ] = useLazyMetricsInspectionsYesterdayQuery();

  const [
    getMetricsEquipmentCountType,
    {
      data: metricsEquipmentCountTypeData,
      error: metricsEquipmentCountTypeError,
      isFetching: metricsEquipmentCountTypeIsLoading,
      isSuccess: metricsEquipmentCountTypeIsSuccess,
      isError: metricsEquipmentCountTypeIsError,
    },
  ] = useLazyMetricsEquipmentCountTypeQuery();

  const [
    getMetricsEquipmentRemoved,
    {
      data: metricsEquipmentRemovedData,
      error: metricsEquipmentRemovedError,
      isFetching: metricsEquipmentRemovedIsLoading,
      isSuccess: metricsEquipmentRemovedIsSuccess,
      isError: metricsEquipmentRemovedIsError,
    },
  ] = useLazyMetricsEquipmentRemovedYesterdayQuery();

  const [
    getMetricsCorrectiveStats,
    {
      data: metricsMetricsCorrectiveStatsData,
      error: metricsMetricsCorrectiveStatsError,
      isFetching: metricsMetricsCorrectiveStatsIsLoading,
      isSuccess: metricsMetricsCorrectiveStatsIsSuccess,
      isError: metricsMetricsCorrectiveStatsIsError,
    },
  ] = useLazyMetricsCorrectiveActionsStatsQuery();

  const [
    getMetricsInspectionByEquipment,
    {
      data: metricsMetricsInspectionByEquipmentData,
      error: metricsMetricsInspectionByEquipmentError,
      isFetching: metricsMetricsInspectionByEquipmentIsLoading,
      isSuccess: metricsMetricsInspectionByEquipmentIsSuccess,
      isError: metricsMetricsInspectionByEquipmentIsError,
    },
  ] = useLazyMetricsInspectionByEquipmentTypeQuery();

  const [
    getMetricsAverageInspectionDur,
    {
      data: metricsMetricsAverageInspectionDurData,
      error: metricsMetricsAverageInspectionDurError,
      isFetching: metricsMetricsAverageInspectionDurIsLoading,
      isSuccess: metricsMetricsAverageInspectionDurIsSuccess,
      isError: metricsMetricsAverageInspectionDurIsError,
    },
  ] = useLazyMetricsAverageInspectionDurationQuery();

  const [
    getMetricsEquipmentDecommission,
    {
      data: metricsMetricsEquipmentDecommissionData,
      error: metricsMetricsEquipmentDecommissionError,
      isFetching: metricsMetricsEquipmentDecommissionIsLoading,
      isSuccess: metricsMetricsEquipmentDecommissionIsSuccess,
      isError: metricsMetricsEquipmentDecommissionIsError,
    },
  ] = useLazyMetricsEquipmentDecommissionsQuery();

  const [
    getDashboardPersonalStats,
    {
      data: dashboardPersonalStatsData,
      error: dashboardPersonalStatsError,
      isFetching: dashboardPersonalStatsIsLoading,
      isSuccess: dashboardPersonalStatsIsSuccess,
      isError: dashboardPersonalStatsIsError,
    },
  ] = useLazyDashboardPersonalStatsQuery();

  const [
    getDashboardPersonalInfo,
    {
      data: dashboardPersonalInfoData,
      error: dashboardPersonalInfoError,
      isFetching: dashboardPersonalInfoIsLoading,
      isSuccess: dashboardPersonalInfoIsSuccess,
      isError: dashboardPersonalInfoIsError,
    },
  ] = useLazyDashboardPersonalInfoQuery();

  const [getAgencyById, {data: agencyData}] = useLazyGetAgencyByIdQuery();

  const [
    getMetricsEquipmentRemovedService,
    {
      data: metricsEquipmentRemovedServiceData,
      error: metricsEquipmentRemovedServiceError,
      isFetching: metricsEquipmentRemovedServiceIsLoading,
      isSuccess: metricsEquipmentRemovedServiceIsSuccess,
      isError: metricsEquipmentRemovedServiceIsError,
    },
  ] = useLazyMetricsEquipmentRemovedQuery();

  useEffect(() => {
    if (equipmentTypesIsSuccess) {
      setEquipmentTypeOptions([
        {value: null, label: t('advancedMetrics.all')},
        ...equipmentTypesData?.map(item => ({
          value: item?.id,
          label: item?.title,
        })),
      ]);
    }
  }, [
    userRolesIsSuccess,
    equipmentTypesIsSuccess,
    t,
    userRolesData,
    equipmentTypesData,
  ]);

  useEffect(() => {
    dispatch(setLoader(true));
    if (currentFbo) {
      const rawPeriod =
        averageFilters?.filter_period ||
        filterPeriodOptions[0]?.value ||
        'weekly';
      const averageParams = new URLSearchParams({
        filter_period: mapFilterPeriodForAverageTime(rawPeriod),
        ...(currentFbo && {agency_id: String(currentFbo)}),
      }).toString();
      Promise.all([
        getMetricsPlanned({agency: currentFbo}),
        getDashboardAveragePerInspection(averageParams),
        getMetricsYesterday({agency: currentFbo}),
        getMetricsEquipmentCountType({agency: currentFbo}),
        getMetricsEquipmentRemoved({agency: currentFbo}),
        getMetricsCorrectiveStats({agency: currentFbo}),
        getMetricsInspectionByEquipment({agency: currentFbo}),
        getMetricsAverageInspectionDur({agency: currentFbo}),
        getMetricsEquipmentDecommission({
          agency: currentFbo,
          period: 'weekly',
        }),
        getDashboardPersonalStats({agency: currentFbo}),
        getDashboardPersonalInfo({name: '', agency: currentFbo}),
        getMetricsEquipmentRemovedService({agency: currentFbo}),
      ])
        .then(res => {
          dispatch(setLoader(false));
        })
        .catch(err => {
          dispatch(setLoader(false));
        });
    }
  }, [currentFbo]);

  useEffect(() => {
    if (currentFbo) {
      getAgencyById(currentFbo);
    }
  }, [currentFbo]);

  useEffect(() => {
    if (
      metricsPlannedIsLoading ||
      metricsYesterdayIsLoading ||
      metricsEquipmentCountTypeIsLoading ||
      metricsEquipmentRemovedIsLoading ||
      metricsMetricsCorrectiveStatsIsLoading ||
      metricsMetricsInspectionByEquipmentIsLoading ||
      metricsMetricsAverageInspectionDurIsLoading ||
      metricsMetricsEquipmentDecommissionIsLoading ||
      dashboardPersonalStatsIsLoading ||
      dashboardPersonalInfoIsLoading
    ) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }
  }, [
    metricsPlannedIsLoading,
    metricsYesterdayIsLoading,
    metricsEquipmentCountTypeIsLoading,
    metricsEquipmentRemovedIsLoading,
    metricsMetricsCorrectiveStatsIsLoading,
    metricsMetricsInspectionByEquipmentIsLoading,
    metricsMetricsAverageInspectionDurIsLoading,
    metricsMetricsEquipmentDecommissionIsLoading,
    dashboardPersonalStatsIsLoading,
    dashboardPersonalInfoIsLoading,
  ]);

  function setColors(data) {
    if (data) {
      let newPieData = [...data];
      const index = newPieData.findIndex(
        item => item.key === 'Out of service (Current)',
      );
      if (index !== -1) {
        const outOfServiceItem = newPieData.splice(index, 1)[0];
        newPieData.unshift(outOfServiceItem);
      }
      for (let i = 0; i <= newPieData.length; i++) {
        for (let j = 0; j <= colors.length; j++) {
          if (i === j) {
            newPieData[i] = {...newPieData[i], color: colors[j]};
          }
        }
      }
      return newPieData;
    }
  }

  const handleFilterMissed = e => {
    setChosenMissedFilters(old => ({...old, period: e.value}));
  };
  const handleFilterMissedSecond = e => {
    setChosenMissedFilters(old => ({...old, filter_for_period: e.value}));
  };

  const handleFilterEquipmentCount = e => {
    getMetricsEquipmentCountType({
      agency: currentFbo,
      equipment_type_id: e.value,
    });
  };

  const handleFilterAverageInspectionEquipment = e => {
    getMetricsEquipmentDecommission({agency: currentFbo, period: e});
  };

  const handleTableChange = e => {
    getDashboardPersonalInfo({agency: currentFbo, name: e.value});
  };

  // const handleFilterNFC = (e) => {
  //   getMetricsInspectionByEquipment({agency: currentFbo, equipment_type_id: e.value})
  // }

  const handleFilterAverageInspectionDuration = e => {
    getMetricsAverageInspectionDur({
      agency: currentFbo,
      equipment_type_id: e,
    });
  };

  const navigateToInspection = id => {
    navigate(`/inspections/${id}`);
  };

  const handleAverageFilter = e => {
    setAverageFilters(old => ({...old, [e.type]: e.value}));
  };

  const metricsEquipmentDecommissionChartData = useMemo(
    () =>
      metricsMetricsEquipmentDecommissionData?.map(item => ({
        ...item,
        key: moment(item.key).format('MM/DD/YYYY'),
      })) ?? [],
    [metricsMetricsEquipmentDecommissionData],
  );

  return (
    <Wrapper>
      <Helmet>
        <title>
          {agencyData?.data?.title
            ? `${agencyData.data.title} - ${t('advancedMetrics.pageTitle')}`
            : t('advancedMetrics.pageTitle')}
        </title>
      </Helmet>
      <DashboardHeader
        title={
          agencyData?.data?.full_title
            ? `${agencyData.data.full_title} - ${t('advancedMetrics.title')}`
            : t('advancedMetrics.title')
        }
        type={'metrics'}
      />
      <div className='metrics'>
        <div className='metrics__head'>
          <div className='metrics__head-back' onClick={() => navigate(-1)}>
            <img src='/assets/icons/chevron-left.svg' alt='back' />
            <span>{t('advancedMetrics.back')}</span>
          </div>
        </div>
        <div className='metrics__block metrics__block--full-grid'>
          <MetricsTable
            headers={tableHeadMockData}
            body={metricsYesterdayData?.inspections}
            cols={'repeat(5, 1fr)'}
            title={t('advancedMetrics.inspectionsCompletedYesterday')}
            showSelect={false}
            width={'shrinked'}
            tableHeight={'metrics-large'}
            rowHeight={'metrics-mid'}
          />
        </div>
        <div className='metrics__block metrics__block--grid-3'>
          <StatisticBlock
            period={'yesterday'}
            data={{
              value:
                metricsMetricsCorrectiveStatsData?.corrective_actions_created,
              percentage:
                metricsMetricsCorrectiveStatsData?.corrective_actions_created_percentage,
            }}
            title={t('advancedMetrics.correctiveActionsCreated')}
            positive={
              metricsMetricsCorrectiveStatsData?.corrective_actions_created_percentage >
              0
                ? true
                : metricsMetricsCorrectiveStatsData?.corrective_actions_created_percentage ==
                    0
                  ? 'grey'
                  : false
            }
            width={'w-30'}
          />
          <StatisticBlock
            period={'yesterday'}
            data={{
              value:
                metricsMetricsCorrectiveStatsData?.corrective_actions_closed,
              percentage:
                metricsMetricsCorrectiveStatsData?.corrective_actions_closed_percentage,
            }}
            title={t('advancedMetrics.correctiveActionsClosedToday')}
            positive={
              metricsMetricsCorrectiveStatsData?.corrective_actions_closed_percentage >
              0
                ? true
                : metricsMetricsCorrectiveStatsData?.corrective_actions_closed_percentage ==
                    0
                  ? 'grey'
                  : false
            }
            width={'w-30'}
          />
          <StatisticBlock
            period={'week'}
            data={{
              value: metricsMetricsCorrectiveStatsData?.average_time,
              percentage:
                metricsMetricsCorrectiveStatsData?.average_time_percentage,
            }}
            title={t('advancedMetrics.averageTimeCorrectiveActionRemainsOpen')}
            positive={
              metricsMetricsCorrectiveStatsData?.average_time_percentage > 0
                ? true
                : metricsMetricsCorrectiveStatsData?.average_time_percentage ==
                    0
                  ? 'grey'
                  : false
            }
            width={'w-30'}
          />
        </div>
        <div className='metrics__block metrics__block--full-grid'>
          <MetricsTable
            headers={tableHead4MockData}
            body={metricsMetricsCorrectiveStatsData?.closed_yesterday_list}
            cols={'1.5fr 1.5fr 1.5fr 1.5fr 2.5fr 2.5fr 2fr'}
            title={t('advancedMetrics.correctiveActionsClosedYesterday')}
            showSelect={false}
            width={'w-100'}
            tableHeight={'metrics-large'}
            rowHeight={'metrics-mid'}
          />
        </div>
        <div className='metrics__block metrics__block--grid-3'>
          <StatisticBlock
            period={'yesterday'}
            title={t('advancedMetrics.equipmentRemovedFromService')}
            data={
              metricsEquipmentRemovedServiceData?.equipment_removed_from_service
            }
            positive={
              metricsEquipmentRemovedServiceData?.equipment_removed_from_service
                ?.percentage > 0
                ? true
                : metricsEquipmentRemovedServiceData
                      ?.equipment_removed_from_service?.percentage == 0
                  ? 'grey'
                  : false
            }
            width={'w-30'}
          />
          <StatisticBlock
            period={'yesterday'}
            title={t('advancedMetrics.equipmentReintroducedIntoService')}
            data={
              metricsEquipmentRemovedServiceData?.equipment_reintroduced_into_service
            }
            positive={
              metricsEquipmentRemovedServiceData
                ?.equipment_reintroduced_into_service?.percentage > 0
                ? true
                : metricsEquipmentRemovedServiceData
                      ?.equipment_reintroduced_into_service?.percentage == 0
                  ? 'grey'
                  : false
            }
            width={'w-30'}
          />
          <StatisticBlock
            period={'week'}
            title={t('advancedMetrics.averageTimeEquipmentStaysOutOfService')}
            data={
              metricsEquipmentRemovedServiceData?.equipment_decommission_average
            }
            positive={
              metricsEquipmentRemovedServiceData?.equipment_decommission_average
                ?.percentage > 0
                ? true
                : metricsEquipmentRemovedServiceData
                      ?.equipment_reintroduced_into_service?.percentage == 0
                  ? 'grey'
                  : false
            }
            width={'w-30'}
          />
        </div>
        <div className='metrics__block metrics__block--full-grid'>
          <MetricsTable
            headers={tableHead3MockData}
            body={metricsEquipmentRemovedData}
            cols={'repeat(5, 1fr)'}
            title={t('advancedMetrics.equipmentRemovedFromServiceYesterday')}
            showSelect={false}
            width={'w-100'}
            tableHeight={'metrics-large'}
            rowHeight={'metrics-mid'}
          />
        </div>
        <div className='metrics__block metrics__block--full-grid'>
          <TinyBarChart
            isTime={true}
            size={'small'}
            width={'w-100'}
            data={metricsEquipmentDecommissionChartData}
            title={t('advancedMetrics.averageTimeEquipmentStaysOutOfService')}
            options={
              filterPeriodShortOptions ? filterPeriodShortOptions : [null]
            }
            handlePeriodChange={handleFilterAverageInspectionEquipment}
          />
        </div>
        <div className='metrics__block metrics__block--grid-2'>
          <AverageTimeBar
            size={'small'}
            data={dashboardAveragePerInspectionData?.data}
            title={t('dashboard.averageTimePerInspection')}
            options={filterPeriodOptions}
            handlePeriodChange={handleAverageFilter}
            setAverageFilters={setAverageFilters}
          />
          <div className='metrics__block-right'>
            <TinyBarChart
              size={'small-mid'}
              width={'w-100'}
              isTime={true}
              options={equipmentTypesData ? equipmentTypeOptions : [null]}
              handlePeriodChange={handleFilterAverageInspectionDuration}
              data={
                metricsMetricsAverageInspectionDurData?.avg_inspection_duration_by_equipment
              }
              title={t(
                'advancedMetrics.averageInspectionDurationByEquipmentType',
              )}
            />
          </div>
        </div>
        <div className='metrics__block metrics__block--full-grid'>
          <MetricsTable
            headers={tableHead5MockData}
            body={metricsMetricsInspectionByEquipmentData}
            cols={'1fr 1fr 1fr 2fr 2fr'}
            title={t('advancedMetrics.nfcAndInspectionMetrics')}
            selectTitle={t('advancedMetrics.equipmentType')}
            showSelect={false}
            width={'w-100'}
            tableHeight={'metrics-small'}
            rowHeight={'metrics-mid'}
            options={equipmentTypesData?.map(item => ({
              value: item.id,
              label: item.title,
            }))}
          />
        </div>
        <div className='metrics__block metrics__block--full-grid'>
          <MetricsTable
            headers={tableHead6MockData}
            body={
              metricsMetricsAverageInspectionDurData?.avg_inspection_duration_by_inspector
            }
            cols={'repeat(3, 1fr)'}
            title={t('advancedMetrics.averageInspectionDurationByInspector')}
            showSelect={false}
            width={'w-100'}
            tableHeight={'metrics-small'}
            rowHeight={'metrics-mid'}
          />
        </div>
      </div>
    </Wrapper>
  );
};

export default AdvancedMetrics;
